import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/Header';
import { connect } from 'react-redux';
import AuthHOC from '../Auth';
import UsersTable from '../../components/tables/UsersTable';
import ConfirmDeleteModal from '../../components/modals/common/ConfirmDeleteModal';
import ConfirmModal from '../../components/modals/common/ConfirmModal';
import { getAll as getAllUsers, remove as deleteUser, patch as updateUserStatus } from '../../services/userService';
import { getAll as getUserGroupLookup } from '../../services/userGroupsService';
import { Stack, Button, Typography, Alert, CircularProgress } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

function UsersView(props) {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [showStatusUpdateModal, setShowStatusUpdateModal] = useState(false);
    const [statusUpdateTarget, setStatusUpdateTarget] = useState(null);
    const [error, setError] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [warningMsg, setWarningMsg] = useState('');

    const { parsed } = props;
    const isAdmin = props.userReducer?.userType?.toLowerCase() === 'admin';

    // Debug: Check what userType value we're getting
    console.log('UsersView - userType:', props.userReducer?.userType, 'isAdmin:', isAdmin);

    const getTitle = () => {
        const localisation = parsed.localisation || {};
        return localisation["menu_header_administration_users"]
            ? localisation["menu_header_administration_users"].element_lang
            : "Administration - Users";
    }

    const handleStatusUpdateCancelClick = () => {
        setShowStatusUpdateModal(false);
        setStatusUpdateTarget(null);
    }

    const handleStatusUpdateUserClick = async () => {
        if (!statusUpdateTarget || !statusUpdateTarget.entity) return;

        const user = statusUpdateTarget.entity;
        const userId = user.id || user._id || user.username;
        try {
            const result = await updateUserStatus(userId, { isActive: (user.isActive ? false : true) });
            if (!result.ok) {
                setError('Failed to update user status. Error status: ' + result.status + " OK: " + result.ok);
                return;
            }
            
            setShowStatusUpdateModal(false);
            setStatusUpdateTarget(null);
            await fetchAllUsers(); // no need to check result, this only refreshes the table.
            setSuccessMsg(`Successfully updated user(s) status.`)
            
        } catch (err) {
            setError('Failed to deactivate/activate user. Error: ' + err.message);
            setShowStatusUpdateModal(false);
            setStatusUpdateTarget(null);
        }
    }

    const fetchAllUsers = useCallback(async () => {
        setIsLoading(true);
        setError('');
        setSuccessMsg('');
        setWarningMsg('');
        try {

            const [userResult, groupsResult] = await Promise.all([
                getAllUsers(),
                getUserGroupLookup()
            ]);

            const groupLookup = {};
            groupsResult.data.forEach(group => {
                groupLookup[group._id] = group;
            });

            if (!userResult.ok) {
                setError('Error loading all users. Error status: ' + userResult.status + " OK: " + userResult.ok);
                return;
            }

            if (!groupsResult.ok) {
                setError('Error loading groups details. Error status: ' + groupsResult.status + " OK: " + groupsResult.ok);
                return;
            }

            const usersWithGroups = userResult.data.map(user => {
                
                const fullGroups = (user.groupIds || [])
                .map(_id => groupLookup[_id])
                .filter(Boolean);
                

                return {
                    ...user,
                    groupOptions: fullGroups.map(g => ({ id: g._id, name: g.name })),
                }
            });         

            setUsers(usersWithGroups || {});
        }
        catch (err) {
            setError('Failed to load users. Error: ' + err.message);
        }
        finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAllUsers();
    }, [fetchAllUsers]);

    // Handle success message from password change redirect
    useEffect(() => {
        const locationState = props.location?.state;
        if (locationState?.successMsg) {
            setSuccessMsg(locationState.successMsg);

            // Auto-dismiss after 5 seconds
            const timer = setTimeout(() => {
                setSuccessMsg('');
            }, 5000);

            // Clear location state to prevent message from showing again on refresh
            window.history.replaceState({}, document.title);

            return () => clearTimeout(timer);
        }
    }, [props.location?.state]);

    const handleDeleteCancel = () => {
        setShowDeleteModal(false);
        setDeleteTarget(null);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget || !deleteTarget.entity) return;

        const user = deleteTarget.entity;
        const userId = user.id || user._id || user.username;
        const username = user.username || user.name || 'user';

        try {
            const result = await deleteUser(userId);
            if (!result.ok) {
                setError('Failed to delete a user. Error status: ' + result.status + " OK: " + result.ok);
                return;
            }
            setShowDeleteModal(false);
            setDeleteTarget(null);
            await fetchAllUsers();
            setSuccessMsg(`Successfully deleted user: ${username}`);
        } catch (err) {
            console.error('Failed to delete user', err);
            setError('Failed to delete user. Error: ' + err.message);
            setShowDeleteModal(false);
            setDeleteTarget(null);
        }
    };

    const handleUserAction = (actionKey, user) => {

        const userId = user.id || user._id || user.username;

        switch (actionKey) {
            case 'edit':
                props.history.push(`/site/administration/UserDetails/${userId}`);
                break;
            case 'deactivate':
                setShowStatusUpdateModal(true);
                setStatusUpdateTarget({ type: 'user', entity: user })
                break;
            case 'delete':
                setShowDeleteModal(true);
                setDeleteTarget({ type: 'user', entity: user });
                break;            
        }
    };

    const handleAddNewUserClick = () => {
        props.history.push("/site/administration/UserDetails");
    }
    const handleImportUsersClick = () => {
        props.history.push("/site/administration/importusers");
    }
    const handleUserGroupsClick = () => {
        props.history.push("/site/administration/UserGroups");
    }

    const title = getTitle();

    return (
        <div className="App">
            <Header visible={!parsed.X3} />
            <div className="page-wrapper">
                <div className="s-page s-users-page">
                    <div className="s-page-header">
                        <Stack 
                            spacing={0.5} 
                            alignItems="flex-start"
                            sx={{ mb: 3, textAlign: "left" }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                {title}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                Users
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Manage user accounts and access
                            </Typography>
                        </Stack>

                        {/* <p className="s-ldp-title">{title}</p> */}
                        <div className="s-page-actions">
                            {isAdmin && (
                                <>
                                    <Button
                                        variant='contained'
                                        startIcon={<AddIcon />}
                                        disableElevation
                                        type="button"
                                        sx={{ textTransform: 'none' }}
                                        onClick={handleAddNewUserClick}
                                        >
                                            Add new
                                    </Button>
                                    <Button
                                        variant='contained'
                                        startIcon={<CloudUploadIcon />}
                                        disableElevation
                                        type="button"
                                        sx={{ textTransform: 'none' }}
                                        onClick={handleImportUsersClick}
                                        >
                                            Import Users
                                    </Button>
                                </>
                            )}
                            <Button
                                variant='outlined'
                                type="button"
                                sx={{
                                    textTransform: 'none',
                                    color: 'text.secondary',
                                    borderColor: 'divider',
                                    '&:hover': {
                                        color: 'primary.main',
                                        borderColor: 'primary.main'
                                    }
                                }}
                                onClick={handleUserGroupsClick}
                                >
                                    User Groups
                            </Button>
                        </div>
                    </div>
                    {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                    )}
                    {successMsg && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                        {successMsg}
                        </Alert>
                    )}
                    {!isAdmin && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            You can edit your own profile. Only administrators can create, edit, or delete other users.
                        </Alert>
                    )}
                    {warningMsg && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                        {warningMsg}
                        </Alert>
                    )}
                    <div className="s-content-panel">
                    <UsersTable
                        users={users}
                        isLoading={isLoading}
                        onAction={handleUserAction}
                        userReducer={props.userReducer}
                    />
                    </div>
                    <ConfirmDeleteModal
                        isOpen={showDeleteModal}
                        entityType={deleteTarget ? deleteTarget.type : ''}
                        entityName={
                            deleteTarget && deleteTarget.entity
                            ? (deleteTarget.entity.username || deleteTarget.entity.name)
                            : ''
                        }
                        onConfirm={handleDeleteConfirm}
                        onCancel={handleDeleteCancel}
                    />
                    <ConfirmModal
                        isOpen={showStatusUpdateModal}
                        mainTitle="Deactivate User?"
                        mainMessage="Are you sure you want to deactivate this user?"
                        textConfirm="CONFIRM"
                        textCancel="CANCEL"
                        onConfirm={handleStatusUpdateUserClick}
                        onCancel={handleStatusUpdateCancelClick}
                        >
                    </ConfirmModal>
                </div>
            </div>
        </div> 
    );
}

export default connect(state => state)(
    AuthHOC(UsersView, { name: 'administration' })
);